class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onViewportResize = this.updateAvailableHeight.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]:not([data-cart-personalize-another])').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.addEventListener('click', this.handlePersonalizeAnother.bind(this));
  }

  open() {
    this.syncPersonalizeAnother();
    this.cancelOpenFocus();
    this.notification.classList.toggle('cart-notification--cube', Boolean(this.querySelector('[data-cube-cart-gallery]')));
    this.updateAvailableHeight();
    window.addEventListener('resize', this.onViewportResize);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.onViewportResize);
    this.notification.classList.add('animate', 'active');

    this.openFocusHandler = () => {
      if (!this.notification.classList.contains('active')) return;
      this.notification.focus();
      trapFocus(this.notification);
    };
    this.notification.addEventListener('transitionend', this.openFocusHandler, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  updateAvailableHeight() {
    if (!this.notification.classList.contains('cart-notification--cube')) {
      this.notification.classList.remove('cart-notification--scroll-all');
      this.notification.style.removeProperty('--gio-notification-height');
      return;
    }
    const wrapper = this.querySelector('.cart-notification-wrapper');
    if (!wrapper) return;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    const available = Math.max(1, Math.floor(bottom - Math.max(0, wrapper.getBoundingClientRect().top) - 12));
    this.notification.style.setProperty('--gio-notification-height', `${available}px`);
    const header = this.querySelector('.cart-notification__header');
    const links = this.querySelector('.cart-notification__links');
    const style = window.getComputedStyle(this.notification);
    const fixedHeight = (header ? header.offsetHeight : 0) + (links ? links.offsetHeight : 0) +
      parseFloat(style.paddingTop || 0) + parseFloat(style.paddingBottom || 0);
    // In short landscape windows even the header and checkout area may not fit.
    // Let the entire panel scroll then, so no action is clipped by the flex layout.
    this.notification.classList.toggle('cart-notification--scroll-all', available < fixedHeight + 96);
  }

  stopViewportWatch() {
    window.removeEventListener('resize', this.onViewportResize);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.onViewportResize);
  }

  cancelOpenFocus() {
    if (this.openFocusHandler) {
      this.notification.removeEventListener('transitionend', this.openFocusHandler);
      this.openFocusHandler = null;
    }
  }

  disconnectedCallback() {
    this.cancelOpenFocus();
    this.stopViewportWatch();
    this.setPersonalizeAnotherContext(null);
    document.body.removeEventListener('click', this.onBodyClick);
  }

  close(options) {
    this.cancelOpenFocus();
    this.stopViewportWatch();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(options && options.restoreFocus === false ? null : this.activeElement);
  }

  setPersonalizeAnotherContext(context) {
    if (this.stopWatchingPersonalizeSource) this.stopWatchingPersonalizeSource();
    this.stopWatchingPersonalizeSource = null;
    this.personalizeAnotherContext = context || null;
    if (context && typeof context.watch === 'function') {
      this.stopWatchingPersonalizeSource = context.watch(() => this.setPersonalizeAnotherContext(null));
    }
    this.syncPersonalizeAnother();
  }

  syncPersonalizeAnother() {
    const button = this.querySelector('[data-cart-personalize-another]');
    const status = this.querySelector('[data-cart-personalize-status]');
    const context = this.personalizeAnotherContext;
    if (button) {
      button.hidden = !(context && context.isValid());
      button.classList.toggle('hidden', button.hidden);
    }
    if (status) {
      status.textContent = '';
      status.hidden = true;
    }
  }

  handlePersonalizeAnother(event) {
    const button = event.target.closest('[data-cart-personalize-another]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    const context = this.personalizeAnotherContext;
    const status = this.querySelector('[data-cart-personalize-status]');
    if (!context || !context.isValid()) {
      this.setPersonalizeAnotherContext(null);
      return;
    }
    if (!context.canStart()) {
      if (status) {
        status.hidden = false;
        status.textContent = 'Please wait while we check your design, then try again.';
      }
      return;
    }
    // Release the cart focus trap before the product designer opens its own.
    this.close({ restoreFocus: false });
    this.setPersonalizeAnotherContext(null);
    let resumed = false;
    try {
      resumed = context.resume(button);
    } catch (error) {
      console.warn('Could not open the product designer:', error);
    }
    if (!resumed) {
      this.setPersonalizeAnotherContext(context);
      this.open();
      if (status) {
        status.hidden = false;
        status.textContent = 'Please try again from the product page.';
      }
    }
  }

  renderContents(parsedState) {
    this.setPersonalizeAnotherContext(null);
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;

    // The personalized-preview lightbox is appended directly to <body>, so it is
    // outside <cart-notification> in the DOM. Treat clicks inside that lightbox as
    // part of the active cart notification; otherwise closing the preview also
    // closes the notification and leaves View cart / Check out unavailable.
    if (target.closest('#CartCustomPreviewLightbox')) return;

    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
